import React from 'react';
import { isNodeSelection } from '@tiptap/react';
import { Editor } from '@tiptap/react';
import { useRichTextTiptapEditor } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-tiptap-editor';
import { RichTextCornerDownLeftIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/corner-down-left-icon';
import { RichTextExternalLinkIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/external-link-icon';
import { RichTextLinkIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/link-icon';
import { RichTextTrashIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/trash-icon';
import { richTextIsMarkInSchema } from 'apps/sigma2-ui/src/components/rich-text/tiptap/lib/tiptap-utils';
import { RichTextButtonProps } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextPopover } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/popover/popover';
import { RichTextPopoverContent } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/popover/popover';
import { RichTextPopoverTrigger } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/popover/popover';
import { RichTextSeparator } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/separator/separator';
// --- Styles ---
import 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui/link-popover/link-popover.scss';

export interface RichTextLinkHandlerProps {
  editor: Editor | null;
  onSetLink?: () => void;
  onLinkActive?: () => void;
}

export interface RichTextLinkMainProps {
  url: string;
  setUrl: React.Dispatch<React.SetStateAction<string | null>>;
  setLink: () => void;
  removeLink: () => void;
  isActive: boolean;
}

export const useLinkHandler = (props: RichTextLinkHandlerProps) => {
  const { editor, onSetLink, onLinkActive } = props;
  const [url, setUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!editor) return;

    // Get URL immediately on mount
    const { href } = editor.getAttributes('link');

    if (editor.isActive('link') && url === null) {
      setUrl(href || '');
      onLinkActive?.();
    }
  }, [editor, onLinkActive, url]);

  React.useEffect(() => {
    if (!editor) return;

    const updateLinkState = () => {
      const { href } = editor.getAttributes('link');
      setUrl(href || '');

      if (editor.isActive('link') && url !== null) {
        onLinkActive?.();
      }
    };

    editor.on('selectionUpdate', updateLinkState);
    return () => {
      editor.off('selectionUpdate', updateLinkState);
    };
  }, [editor, onLinkActive, url]);

  const setLink = React.useCallback(() => {
    if (!url || !editor) return;

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();

    setUrl(null);

    onSetLink?.();
  }, [editor, onSetLink, url]);

  const removeLink = React.useCallback(() => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .unsetLink()
      .setMeta('preventAutolink', true)
      .run();
    setUrl('');
  }, [editor]);

  return {
    url: url || '',
    setUrl,
    setLink,
    removeLink,
    isActive: editor?.isActive('link') || false,
  };
};

export const RichTextLinkButton = React.forwardRef<
  HTMLButtonElement,
  RichTextButtonProps
>(({ className, children, ...props }, ref) => {
  return (
    <RichTextButton
      type="button"
      className={className}
      data-style="ghost"
      role="button"
      tabIndex={-1}
      aria-label="Link"
      tooltip="Link"
      ref={ref}
      {...props}
    >
      {children || <RichTextLinkIcon className="tiptap-button-icon" />}
    </RichTextButton>
  );
});

export const RichTextLinkContent: React.FC<{
  editor?: Editor | null;
}> = ({ editor: providedEditor }) => {
  const editor = useRichTextTiptapEditor(providedEditor);

  const linkHandler = useLinkHandler({
    editor: editor,
  });

  return <LinkMain {...linkHandler} />;
};

const LinkMain: React.FC<RichTextLinkMainProps> = ({
  url,
  setUrl,
  setLink,
  removeLink,
  isActive,
}) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      setLink();
    }
  };

  return (
    <>
      <input
        type="url"
        placeholder="Paste a link..."
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        className="form-input tiptap-input tiptap-input-clamp"
      />

      <div className="tiptap-button-group" data-orientation="horizontal">
        <RichTextButton
          type="button"
          onClick={setLink}
          title="Apply link"
          disabled={!url && !isActive}
          data-style="ghost"
        >
          <RichTextCornerDownLeftIcon className="tiptap-button-icon" />
        </RichTextButton>
      </div>

      <RichTextSeparator />

      <div className="tiptap-button-group" data-orientation="horizontal">
        <RichTextButton
          type="button"
          onClick={() => window.open(url, '_blank')}
          title="Open in new window"
          disabled={!url && !isActive}
          data-style="ghost"
        >
          <RichTextExternalLinkIcon className="tiptap-button-icon" />
        </RichTextButton>

        <RichTextButton
          type="button"
          onClick={removeLink}
          title="Remove link"
          disabled={!url && !isActive}
          data-style="ghost"
        >
          <RichTextTrashIcon className="tiptap-button-icon" />
        </RichTextButton>
      </div>
    </>
  );
};

export interface RichTextLinkPopoverProps
  extends Omit<RichTextButtonProps, 'type'> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor | null;
  /**
   * Whether to hide the link popover.
   * @default false
   */
  hideWhenUnavailable?: boolean;
  /**
   * Callback for when the popover opens or closes.
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Whether to automatically open the popover when a link is active.
   * @default true
   */
  autoOpenOnLinkActive?: boolean;
}

export function RichTextLinkPopover({
  editor: providedEditor,
  hideWhenUnavailable = false,
  onOpenChange,
  autoOpenOnLinkActive = true,
  ...props
}: RichTextLinkPopoverProps) {
  const editor = useRichTextTiptapEditor(providedEditor);

  const linkInSchema = richTextIsMarkInSchema('link', editor);

  const [isOpen, setIsOpen] = React.useState(false);

  const onSetLink = () => {
    setIsOpen(false);
  };

  const onLinkActive = () => setIsOpen(autoOpenOnLinkActive);

  const linkHandler = useLinkHandler({
    editor: editor,
    onSetLink,
    onLinkActive,
  });

  const isDisabled = React.useMemo(() => {
    if (!editor) return true;
    if (editor.isActive('codeBlock')) return true;
    return !editor.can().setLink?.({ href: '' });
  }, [editor]);

  const canSetLink = React.useMemo(() => {
    if (!editor) return false;
    try {
      return editor.can().setMark('link');
    } catch {
      return false;
    }
  }, [editor]);

  const isActive = editor?.isActive('link') ?? false;

  const handleOnOpenChange = React.useCallback(
    (nextIsOpen: boolean) => {
      setIsOpen(nextIsOpen);
      onOpenChange?.(nextIsOpen);
    },
    [onOpenChange],
  );

  const show = React.useMemo(() => {
    if (!linkInSchema || !editor) {
      return false;
    }

    if (hideWhenUnavailable) {
      if (isNodeSelection(editor.state.selection) || !canSetLink) {
        return false;
      }
    }

    return true;
  }, [linkInSchema, hideWhenUnavailable, editor, canSetLink]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <RichTextPopover open={isOpen} onOpenChange={handleOnOpenChange}>
      <RichTextPopoverTrigger asChild>
        <RichTextLinkButton
          disabled={isDisabled}
          data-active-state={isActive ? 'on' : 'off'}
          data-disabled={isDisabled}
          {...props}
        />
      </RichTextPopoverTrigger>

      <RichTextPopoverContent>
        <LinkMain {...linkHandler} />
      </RichTextPopoverContent>
    </RichTextPopover>
  );
}

RichTextLinkButton.displayName = 'LinkButton';
